import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CATEGORIES, TOOLBAR, SHARED_TOP, SHARED_BOTTOM,
  STICKER_GLYPHS, EMOJI_GLYPHS,
} from "./toolConfig";
import { TEAL, MARKER_COLORS } from "./constants";
import { IconChevronLeft, IconChevronRight, FlyoutMarker } from "../../lib/Icons/WhiteboardIcons";

const HOLD_MS = 320;

export default function WhiteboardToolbar({
  category, onCategory,
  tool, onTool,
  color, onColor,
  size, onSize,
  gridMode,
  canUndo, canRedo,
  onAction, onFramework,
  glyph, onGlyph,
}) {
  const [flyout, setFlyout] = useState(null);
  const [picker, setPicker] = useState(null);
  const [slotVariant, setSlotVariant] = useState({});

  const holdTimer = useRef(null);
  const didHold = useRef(false);
  const rootRef = useRef(null);

  const closePopovers = useCallback(() => { setFlyout(null); setPicker(null); }, []);

  useEffect(() => {
    if (!flyout && !picker) return;
    const close = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) closePopovers();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [flyout, picker, closePopovers]);

  useEffect(() => () => clearTimeout(holdTimer.current), []);

  const categoryIndex = CATEGORIES.findIndex((c) => c.id === category);

  const offsetOf = (el) => {
    const slotEl = el?.closest?.("[data-slot]");
    if (!slotEl || !rootRef.current) return 0;
    return slotEl.getBoundingClientRect().top - rootRef.current.getBoundingClientRect().top;
  };

  const cycle = (dir) => {
    const next = (categoryIndex + dir + CATEGORIES.length) % CATEGORIES.length;
    onCategory(CATEGORIES[next].id);
    closePopovers();
  };

  const activate = useCallback((slot, originEl) => {
    if (slot.soon) return;
    setFlyout(null);
    if (slot.kind === "tool") {
      onTool(slot.id);
      if (slot.id === "sticker" || slot.id === "emoji") {
        setPicker({ toolId: slot.id, top: offsetOf(originEl) });
      } else {
        setPicker(null);
      }
      return;
    }
    if (slot.kind === "framework") { onFramework(slot.id); return; }
    if (slot.kind === "action")    { onAction(slot.id); }
  }, [onTool, onFramework, onAction]);

  const startHold = (slot, originEl) => {
    if (!slot.flyout?.length) return;
    didHold.current = false;
    const top = offsetOf(originEl);
    clearTimeout(holdTimer.current);
    holdTimer.current = setTimeout(() => {
      didHold.current = true;
      setPicker(null);
      setFlyout({ slotId: slot.id, top });
    }, HOLD_MS);
  };

  const endHold = () => clearTimeout(holdTimer.current);

  const isActive = (slot) => {
    if (slot.kind === "tool") return tool === slot.id;
    if (slot.id === "grid") return gridMode !== "off";
    return false;
  };

  const isDisabled = (slot) => {
    if (slot.soon) return true;
    if (slot.id === "undo") return !canUndo;
    if (slot.id === "redo") return !canRedo;
    return false;
  };

  const renderSlot = (slot, wide) => {
    const shown = slotVariant[slot.id] || slot;
    const active = isActive(shown);
    const disabled = isDisabled(shown);
    const Icon = shown.Icon;
    const hasVariants = Boolean(slot.flyout?.length);

    return (
      <div key={slot.id} data-slot={slot.id} className="relative flex-1">
        <button
          type="button"
          title={shown.soon
            ? `${shown.label} — ${typeof shown.soon === "string" ? shown.soon : "coming in a later phase"}`
            : `${shown.label}${shown.hint ? `  (${shown.hint})` : ""}${hasVariants ? "  · hold for more" : ""}`}
          disabled={disabled}
          onMouseDown={(e) => startHold(slot, e.currentTarget)}
          onMouseUp={endHold}
          onMouseLeave={endHold}
          onClick={(e) => {
            if (didHold.current) { didHold.current = false; return; }
            activate(shown, e.currentTarget);
          }}
          className={`w-full ${slot.showLabel ? "py-[7px]" : "h-[38px]"} rounded-[10px] flex flex-col items-center justify-center gap-[2px] transition-all duration-100 border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4DB8B5] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          }`}
          style={{
            background: active ? "rgba(77,184,181,0.16)" : "rgba(255,255,255,0.55)",
            borderColor: active ? "rgba(77,184,181,0.5)" : "rgba(0,0,0,0.06)",
            color: disabled ? "#CBD5E1" : active ? TEAL : "#334155",
            boxShadow: active ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
            opacity: disabled ? 0.55 : 1,
          }}
        >
          <Icon size={wide ? 19 : 20} />
          {slot.showLabel && (
            <span className="text-[8.5px] font-semibold font-['DM_Sans'] leading-none tracking-[0.01em]">
              {shown.label}
            </span>
          )}
        </button>

        {hasVariants && (
          <span
            onMouseDown={(e) => {
              e.stopPropagation();
              const top = offsetOf(e.currentTarget);
              setPicker(null);
              setFlyout((prev) => (prev?.slotId === slot.id ? null : { slotId: slot.id, top }));
            }}
            title={`More ${shown.label} options`}
            className="absolute bottom-[3px] right-[3px] text-slate-400 pointer-events-auto cursor-pointer"
          >
            <FlyoutMarker />
          </span>
        )}
      </div>
    );
  };

  const renderRows = (rows) =>
    rows.map((row, i) => {
      const wide = row.length > 1;
      if (row[0]?.kind === "colors") {
        return (
          <div key={`colors-${i}`} className="flex items-center justify-between px-[3px] py-[5px]">
            {MARKER_COLORS.map((mc) => (
              <button
                key={mc.hex}
                type="button"
                title={mc.label}
                onClick={() => onColor(mc.hex)}
                className="rounded-full transition-transform duration-100"
                style={{
                  width: color === mc.hex ? 13 : 10,
                  height: color === mc.hex ? 13 : 10,
                  background: mc.hex,
                  boxShadow: color === mc.hex ? `0 0 0 2px white, 0 0 0 3.5px ${mc.hex}` : "none",
                }}
              />
            ))}
          </div>
        );
      }
      if (row[0]?.kind === "size") {
        return (
          <div key={`size-${i}`} className="flex items-center gap-[6px] px-[4px] py-[3px]">
            <span className="text-[9px] font-bold text-slate-400 font-['DM_Sans']">SZ</span>
            <input
              type="range" min={1} max={20} value={size}
              onChange={(e) => onSize(+e.target.value)}
              className="flex-1 w-full cursor-pointer"
              style={{ accentColor: TEAL }}
            />
          </div>
        );
      }
      return (
        <div key={`row-${i}`} className="flex gap-[5px]">
          {row.map((slot) => renderSlot(slot, wide))}
        </div>
      );
    });

  const findSlot = (slotId) => {
    const all = [...SHARED_TOP, ...(TOOLBAR[category] || []), ...SHARED_BOTTOM].flat();
    return all.find((s) => s.id === slotId) || null;
  };

  const flyoutSlot = flyout ? findSlot(flyout.slotId) : null;
  const flyoutVariants = flyoutSlot ? [flyoutSlot, ...(flyoutSlot.flyout || [])] : [];

  return (
    <div
      ref={rootRef}
      className="relative w-[86px] flex-shrink-0 flex flex-col gap-[5px] p-[8px] rounded-[16px] font-['DM_Sans'] select-none"
      style={{
        background: "rgba(253,251,247,0.82)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 8px 28px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.7)",
        zIndex: 40,
      }}
    >
      <div className="flex items-center justify-between px-[2px] text-slate-500">
        <button type="button" onClick={() => cycle(-1)} title="Previous category"
          className="w-[24px] h-[22px] flex items-center justify-center rounded-[6px] hover:bg-slate-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4DB8B5]">
          <IconChevronLeft size={16} />
        </button>
        <button type="button" onClick={() => cycle(1)} title="Next category"
          className="w-[24px] h-[22px] flex items-center justify-center rounded-[6px] hover:bg-slate-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4DB8B5]">
          <IconChevronRight size={16} />
        </button>
      </div>

      <div
        className="text-center text-[11px] font-bold text-white rounded-full py-[4px] tracking-[0.01em]"
        style={{ background: TEAL, boxShadow: "0 2px 6px rgba(77,184,181,0.4)" }}
      >
        {CATEGORIES[categoryIndex]?.label}
      </div>

      {renderRows(SHARED_TOP)}

      <div className="flex flex-col gap-[5px] overflow-y-auto min-h-0 flex-1 whiteboard-tool-scroll">
        {renderRows(TOOLBAR[category] || [])}
      </div>

      {renderRows(SHARED_BOTTOM)}

      <div className="flex items-center justify-center gap-[5px] pt-[2px]">
        {CATEGORIES.map((c, i) => (
          <button
            key={c.id}
            type="button"
            title={c.label}
            onClick={() => { onCategory(c.id); closePopovers(); }}
            className="rounded-full cursor-pointer"
            style={{
              width: 6, height: 6,
              background: i === categoryIndex ? TEAL : "rgba(0,0,0,0.15)",
            }}
          />
        ))}
      </div>

      {flyoutSlot && (
        <div
          className="absolute z-[300] flex gap-[3px] p-[4px] rounded-[10px] border border-black/[0.07] shadow-[0_6px_20px_rgba(0,0,0,0.16)]"
          style={{ left: "calc(100% + 7px)", top: flyout.top, background: "#FFFFFF" }}
        >
          {flyoutVariants.map((v) => {
            const VIcon = v.Icon;
            return (
              <button
                key={v.id}
                type="button"
                title={v.label}
                disabled={!!v.soon}
                onClick={(e) => {
                  if (v.soon) return;
                  setSlotVariant((prev) => ({ ...prev, [flyoutSlot.id]: v }));
                  activate(v, e.currentTarget);
                }}
                className="w-[34px] h-[34px] rounded-[8px] flex items-center justify-center border border-transparent hover:border-black/10 hover:bg-slate-50 focus:outline-none focus-visible:border-[#4DB8B5]"
                style={{
                  color: v.soon ? "#CBD5E1" : tool === v.id ? TEAL : "#334155",
                  cursor: v.soon ? "not-allowed" : "pointer",
                }}
              >
                <VIcon size={18} />
              </button>
            );
          })}
        </div>
      )}

      {picker && (
        <div
          className="absolute z-[300] w-[188px] p-[8px] rounded-[10px] border border-black/[0.07] shadow-[0_6px_20px_rgba(0,0,0,0.16)]"
          style={{ left: "calc(100% + 7px)", top: picker.top, background: "#FFFFFF" }}
        >
          <div className="px-[2px] pb-[6px]">
            <div className="text-[11px] font-bold text-slate-700 leading-none">
              {picker.toolId === "sticker" ? "Stickers" : "Emoji"}
            </div>
            <div className="text-[10px] text-slate-400 leading-none pt-[3px]">
              Pick one, then click the board
            </div>
          </div>
          <div className="grid grid-cols-6 gap-[3px]">
            {(picker.toolId === "sticker" ? STICKER_GLYPHS : EMOJI_GLYPHS).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onGlyph(g)}
                className="h-[26px] rounded-[6px] text-[16px] leading-none flex items-center justify-center cursor-pointer border border-transparent hover:bg-slate-100 focus:outline-none focus-visible:border-[#4DB8B5]"
                style={{ background: glyph === g ? "rgba(77,184,181,0.18)" : "transparent" }}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .whiteboard-tool-scroll::-webkit-scrollbar { width: 4px; }
        .whiteboard-tool-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.14); border-radius: 3px; }
      `}</style>
    </div>
  );
}
